import { ApplicationSettings, AppSetting, CreateAppSettingRequest, SettingsClient, UpdateAppSettingRequest } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const settingsClient = new SettingsClient(undefined, axios);

export const useAppSettings = (options?: any) => {
  return useQuery<ApplicationSettings | null>({
    ...options,
    queryKey: ["appSettings"],
    queryFn: async () => settingsClient.getApplicationSettings(),
  });
};

export const useGetAppSetting = (key: string, options?: any) => {
  return useQuery<AppSetting | null>({
    ...options,
    queryKey: ["appSetting", key],
    queryFn: async () => settingsClient.getAppSetting(key),
  });
};

export const useUpdateAppSetting = (options?: any) => {
  return useMutation<AppSetting, Error, { key: string; value: string }>({
    ...options,
    mutationFn: ({ key, value }: { key: string; value: string }) => {
      const request = new UpdateAppSettingRequest({ value });
      return settingsClient.updateAppSetting(key, request);
    },
    onSuccess: async (data: AppSetting) => {
      // Invalidate the appSettings query to refetch with updated data
      await queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      await queryClient.invalidateQueries({ queryKey: ["appSetting", data.key] });
      options?.onSuccess?.(data);
    },
  });
};

export const useCreateAppSetting = (options?: any) => {
  return useMutation<AppSetting, Error, { key: string; value: string; description?: string }>({
    ...options,
    mutationFn: ({ key, value, description }) => {
      const request = new CreateAppSettingRequest({ key, value, description });
      return settingsClient.createAppSetting(request);
    },
    onSuccess: async (data: AppSetting) => {
      // Invalidate the appSettings query to refetch with new setting
      await queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      queryClient.setQueryData(["appSetting", data.key], data);
      options?.onSuccess?.(data);
    },
  });
};

export const useDeleteAppSetting = (options?: any) => {
  return useMutation<void, Error, string>({
    ...options,
    mutationFn: (key: string) => settingsClient.deleteAppSetting(key),
    onSuccess: async (_, key: string) => {
      // Invalidate the appSettings query to refetch without deleted setting
      await queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      await queryClient.invalidateQueries({ queryKey: ["appSetting", key] });
      options?.onSuccess?.();
    },
  });
};
